import React, { ReactNode, useState } from 'react';
import { useForm, Controller, ControllerProps } from 'react-hook-form';
import FormModal from 'v2/common/FormModal/FormModal';
import useFormFieldMutation from '../hooks/useFormFieldMutation';
import { DrmPage } from 'v2/models';
import FieldValueRenderer from '../FieldValueRenderer/FieldValueRenderer';
import SectionHeader from 'v2/common/DRM/SectionHeader/SectionHeader';
import classNames from 'classnames';
import styles from '../field.module.scss';

interface FieldFormFields {
  content: string;
}

interface FieldFormProps {
  entity: Record<string, unknown>;
  fieldName: string;
  fieldLabel: string;
  onUpdate: (entity: Record<string, unknown>) => void;
  drmPage: DrmPage;
  renderInput: ControllerProps<FieldFormFields, 'content'>['render'];
  content: ReactNode;
  value: string[] | string | number | boolean | null;
  formLabel?: string;
}

const FieldForm = ({
  entity,
  fieldName,
  fieldLabel,
  onUpdate,
  drmPage,
  content,
  renderInput,
  value,
  formLabel,
}: FieldFormProps) => {
  const [showForm, setShowForm] = useState(false);
  const formContext = useForm<FieldFormFields>();

  const { control } = formContext;

  const onClose = () => setShowForm(false);

  const { mutate: updateFieldValue } = useFormFieldMutation({
    fieldName,
    fieldLabel,
    entity,
    drmPage,
    onUpdate,
    onClose,
  });

  const handleSubmit = ({ content }: FieldFormFields) => {
    updateFieldValue(content);
  };

  const defaultValue = String(value ?? '');

  return (
    <>
      <SectionHeader
        title={fieldLabel}
        isEditable={Boolean(entity.is_editable)}
        onClick={() => setShowForm(true)}
        className={classNames(styles.sectionHeader)}
      />
      <FieldValueRenderer
        fieldLabel={fieldLabel}
        content={content}
        hasValue={value !== null}
      />
      <FormModal
        show={showForm}
        title={`Edit ${fieldLabel}`}
        onClose={onClose}
        onSubmit={handleSubmit}
        formContext={formContext}
      >
        <div>
          <label htmlFor={fieldName}>{formLabel || fieldLabel}</label>
          <Controller
            name="content"
            control={control}
            defaultValue={defaultValue}
            render={renderInput}
          />
        </div>
      </FormModal>
    </>
  );
};

export default FieldForm;
